/**
 * @module MuiDesignContainer
 * @description Material UI version of the main container component for the design view in the Master Designer application.
 * It manages the layout and conditional rendering of various sub-components based on the current application state.
 */
import React from 'react';
import { Box, styled } from '@mui/material';
import { useTheme } from 'theme/ThemeContext';
import { useSelector } from "react-redux";
import MuiActionBar from './ActionBar';
import MuiSVGContainer from './SVGContainer';
import { MuiUnitSwitch } from '../molecules';
import { MuiScale } from '../molecules';
import EquipmentListManager from 'components/services/EquipmentTableManager';
import { MuiBubble } from '../atoms';
import { getShowMap, getCurrentView, getDrawingBox } from 'reduxModules/ducks/ui';
import { DesignContainerProps } from 'types/components/organisms';
import ActionButtons from './ActionButtons';
import type { RootState } from 'types/redux/state';

/**
 * @function MuiDesignContainer
 * @description Renders the main design container with conditional sub-components based on the current view and application state.
 * @returns The rendered MuiDesignContainer component
 */
const MuiDesignContainer: React.FC<DesignContainerProps> = () => {
  // Get theme mode
  const { mode } = useTheme();
  
  // Select relevant state from Redux store
  const currentView = useSelector((state: any) => (getCurrentView as any)(state)) as string; // active menu
  const equipmentList = currentView === 'equipmentlist';

  const showMap = useSelector((state: any) => (getShowMap as any)(state)) as boolean;
  
  // Show legend bar for microphone, speaker, or camera views regardless of showMap
  const showLegend = showMap || currentView === 'microphones' || currentView === 'speakers' || currentView === 'camera';

  // Get the floorplan bounding box for positioning buttons relative to the drawn floorplan
  const drawingBox = useSelector((state: any) => (getDrawingBox as any)(state));
  const bbox = drawingBox?.bbox;

  return (
    <Wrapper data-tooltip-id="containerTooltip">
      {!equipmentList && (
        <>
          <DynamicWrapper>
            {/* Tool button positioned at bottom-left of design area */}
            <ToolButtonContainer>
              <MuiActionBar />
            </ToolButtonContainer>

            {/* Units toggle positioned at top-left of design area */}
            <UnitsToggleContainer>
              <MuiUnitSwitch nomargin />
            </UnitsToggleContainer>

            {/* Main content area with floorplan */}
            <FloorplanArea>
              {/* HeatMapManager is rendered INSIDE MuiSVGContainer as a background layer behind
                  the SVG grid (see SVGContainer, zIndex 0 vs the SVG's zIndex 100), so the tile
                  grid stays crisp on top of the coverage. A second HeatMapManager used to be
                  rendered here, on top of the SVG — it painted the translucent coverage OVER the
                  grid, washing the thin grid lines and making them look doubled (worse under
                  fractional display scaling). Removed to keep the grid above the coverage. */}
              <MuiSVGContainer />
            </FloorplanArea>

            {/* Legend positioned below floorplan - shown for device views */}
            <LegendArea>
              {showLegend && <MuiScale />}
            </LegendArea>
          </DynamicWrapper>

          {/* Action buttons at the bottom */}
          <ActionButtons position="relative" />
        </>
      )}

      {/* Equipment list view with action buttons */}
      {equipmentList && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box component={EquipmentListManager as any} sx={{ flex: 1, overflow: 'auto' }} />
          <ActionButtons position="relative" />
        </Box>
      )}

      {/* Removed place prop as it's not in BubbleProps */}
      {!equipmentList && <MuiBubble id="containerTooltip" />}
    </Wrapper>
  );
};

// Styled components using MUI styled API
const Wrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  padding: 0,
}));

const DynamicWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  flexGrow: 1,
  width: '100%',
  backgroundColor: theme.palette.background.default,  // Use theme background
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRadius: '0px',
  overflow: 'hidden',
}));

const FloorplanArea = styled(Box)(({ theme }) => ({
  position: 'relative',
  flexGrow: 1,
  width: '100%',
  minHeight: 0, // Allow flex shrinking
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  borderRadius: '8px 8px 0 0', // Rounded top corners only
}));

const LegendArea = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '90px', // Fixed height instead of minHeight to maintain consistent size
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  backgroundColor: theme.palette.background.default, // Use theme background
  borderRadius: '0 0 8px 8px', // Rounded bottom corners only
  zIndex: 1000,
  flexShrink: 0, // Prevent the area from shrinking
}));

const UnitsToggleContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '32px',  // Corrected to match Figma: 32px from top
  left: '46px',
  zIndex: 1001,
  background: 'transparent',
  pointerEvents: 'auto',
}));

const ToolButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '46px',
  top: '82px',  // 82px from top, giving 20px gap after unit toggle (32px + 30px height + 20px gap)
  zIndex: 1001,
  background: 'transparent',
  pointerEvents: 'auto',
}));




export default MuiDesignContainer;
